import React, { useState, memo, useEffect } from "react";
import { Form, Button, Table } from "react-bootstrap";
import { useSelector } from "react-redux";

import HourLogAPIs from "../../../../APIs/hour-log";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { UNITS_INITIAL_COLUMNS } from "config/constants";

const PerTableTab = memo(({ logDate, toggleRefresh }) => {
  const [ticketNo, setTicketNo] = useState("");
  const [tickets, setTickets] = useState([]);
  const [userId, setUserId] = useState("");

  //  read user from redux
  const reduxUser = useSelector((state) => state.auth.user);

  //  permission check
  const permissions = reduxUser?.department?.permissions ?? [];
  const canDelete = permissions.includes("delete_log");

  const checkTicketStatus = async () => {
    const response = await HourLogAPIs.AHE_PerTicketStatus({
      userId,
      ticketNo,
      logType: "PER",
    });

    if (response.data?.data?.status) return true;

    toast.error(response?.data?.message || "Something went wrong!");
    return false;
  };

  const handleAddTicket = async () => {
    if (!ticketNo) {
      toast.warn("Please enter a ticket number.");
      return;
    }

    const data = {
      logType: "PER_TICKET",
      ticketNo,
      startDateTime: new Date(logDate).toISOString(),
      endDateTime: new Date(logDate).toISOString(),
    };

    try {
      if (!(await checkTicketStatus())) return;

      const response = await HourLogAPIs.addHourLog(data);
      if (response.data?.success) {
        toast.success("Hour Log Added Successfully");
        setTicketNo("");

        await fetchQueryHourLogs({
          date: format(new Date(logDate), "yyyy-MM-dd"),
          logType: "PER_TICKET",
          userId,
        });

        toggleRefresh?.();
      } else {
        toast.warn(response?.message);
      }
    } catch (error) {
      console.error("Error adding hour log:", error);
    }
  };

 
// fetch logs
  const fetchQueryHourLogs = async (data) => {
    try {
      const response = await HourLogAPIs.getQueryHourLog(data);

      if (response?.data?.data?.length > 0) {
        const extractedTickets = response.data.data.flatMap(
          (log) => log.hourLogDetails
        );
        setTickets(extractedTickets);
      } else {
        setTickets([]);
      }
    } catch (error) {
      console.error("Error fetching hour logs:", error);
      setTickets([]);
    }
  };

  const handleDelete = async (hourLogDetailId, event) => {
    event.preventDefault();
    event.stopPropagation();

    const isConfirmed = window.confirm("Are you sure you want to delete this Ticket?");
    if (!isConfirmed) return;

    const data = {
      hourLogDetailId,
      date: logDate,
      logType: "PER_TICKET",
    };

    try {
      const response = await HourLogAPIs.deleteHourLog(data);

      if (response?.data?.data) {
        toast.success("Ticket Deleted Successfully");
        await fetchQueryHourLogs({
          date: format(new Date(logDate), "yyyy-MM-dd"),
          logType: "PER_TICKET",
          userId,
        });
      }
    } catch (error) {
      console.error("Error deleting ticket:", error);
    }
  };

  // ✅ use redux user (instead of localStorage)
  useEffect(() => {
    if (!reduxUser?.id) return;

    setUserId(reduxUser.id);

    fetchQueryHourLogs({
      date: format(new Date(logDate), "yyyy-MM-dd"),
      logType: "PER_TICKET",
      userId: reduxUser.id,
    });
  }, [reduxUser?.id, logDate]);

  const formatColumnName = (columnName) => {
    return columnName
      .replace(/([A-Z]+)/g, " $1")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
      .trim();
  };

  const formatValue = (ticket, columnName) => {
    if (columnName.includes("Unit")) {
      return ticket.ticketPreComplete &&
        ticket.ticketPreComplete[columnName] &&
        !["NaN"].includes(ticket.ticketPreComplete[columnName])
        ? ticket.ticketPreComplete[columnName]
        : "0";
    }

    return ticket[columnName] === null || ticket[columnName] === undefined
      ? "0"
      : ticket[columnName];
  };

  const columns = tickets.reduce((acc, ticket) => {
    Object.keys(ticket?.ticketPreComplete || {}).forEach((key) => {
      if (key.includes("Unit") && !acc.includes(key)) acc.push(key);
    });

    return acc.some((item) => item.includes("Unit"))
      ? acc
      : [...acc, ...UNITS_INITIAL_COLUMNS];
  }, []);

  return (
    <div>
      <Form.Group className="mb-3" controlId="ticketNo">
        <Form.Label>Ticket Number</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter ticket number"
          value={ticketNo}
          onChange={(e) => setTicketNo(e.target.value)}
        />
      </Form.Group>

      <Button variant="primary" onClick={handleAddTicket}>
        Add Ticket
      </Button>

      <div style={{ overflowX: "auto", width: "100%" }}>
        <Table striped bordered hover className="mt-3">
          <thead>
            <tr>
              <th>Ticket Number</th>
              {columns.map((column) => (
                <th key={column}>{formatColumnName(column)}</th>
              ))}
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {tickets.map((ticket, index) => (
              <tr key={ticket.id || index}>
                <td>{ticket.ticketNo}</td>
                {columns.map((column) => (
                  <td key={column}>{formatValue(ticket, column)}</td>
                ))}

                <td className="align-middle text-center">
                  {/*  show delete only if delete_log permission exists */}
                  {canDelete && (
                    <Button variant="danger" onClick={(e) => handleDelete(ticket.id, e)}>
                      Delete
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
});

export default PerTableTab;